import {
  useV2ListBlueprintsForAccountQuery,
  V2ProjectServiceResource,
  V2ProjectSubserviceResource,
  V2ProjectResourceResource,
  V2ProjectPhaseResource,
  V2ProjectResource,
  V2ProjectGovernanceResource,
  V2ProjectExpenseResource,
  V2ProjectProductResource,
} from "@generated";
import {
  Blueprint,
  BlueprintExpense,
  BlueprintGeneral,
  BlueprintGovernance,
  BlueprintProduct,
  BlueprintResource,
  Subservice,
} from "../../types";

// Payload entries are a flat array of V2 project-* envelopes that reference
// each other by id within the same payload.
type PayloadEnvelope = {
  id?: number | string;
  type?: string;
  attributes?: any;
  relationships?: any;
};

const useListBlueprints = ({
  pageSize,
  currentPage,
  filterParams,
  sortBy,
  sortOrder,
}) => {
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  const { data, error, isLoading, refetch, isFetching } =
    useV2ListBlueprintsForAccountQuery({
      page: { number: currentPage, size: pageSize },
      filter: { ...filterParams },
      sort: sortBy ? [sortOrder == "asc" ? sortBy : `-${sortBy}`] : undefined,
    });

  let blueprints: Blueprint[] = [];

  if (data?.data) {
    try {
      blueprints = data.data.map((blueprint) => {
        if (!blueprint.id) {
          throw new Error("Missing blueprint.id");
        }
        if (!blueprint.attributes?.name) {
          throw new Error("Missing blueprint.attributes.name");
        }

        const payload: PayloadEnvelope[] =
          (blueprint.attributes?.payload as PayloadEnvelope[] | undefined) ||
          [];

        // Group services by phase NAME (sequential numeric ids per blueprint)
        // so the existing { id, name, position } Phase shape and the id-based
        // grouping helper keep working for custom + catalog phases alike.
        const phaseIdByName = new Map<string, number>();
        const phaseIdFor = (name: string) => {
          if (!phaseIdByName.has(name)) {
            phaseIdByName.set(name, phaseIdByName.size + 1);
          }
          return phaseIdByName.get(name)!;
        };

        // Resource name comes from the inline project-resource envelope in
        // the payload. Catalog resources can't be side-loaded on this endpoint
        // anymore, so rows that only reference a catalog `resource` (without a
        // matching project-resource envelope) will render without a name.
        const lookupResource = (
          projectResourceId: string | number | undefined
        ): { id: string; name: string } | undefined => {
          if (projectResourceId == null) return undefined;
          const pr = payload.find(
            (p) =>
              p.type === "project-resources" &&
              String(p.id) === String(projectResourceId)
          ) as V2ProjectResourceResource | undefined;
          if (!pr?.id) return undefined;
          return {
            id: `project-resources-${pr.id}`,
            name: pr.attributes?.name || "",
          };
        };

        const projectServices = payload.filter(
          (p) => p.type === "project-services"
        ) as V2ProjectServiceResource[];

        const services: Blueprint["services"] = projectServices.map((ps) => {
          const serviceType =
            ps.attributes?.["service-type"] || "professional_services";

          const projectPhaseId = ps.relationships?.["project-phase"]?.data?.id;
          const projectPhase = projectPhaseId
            ? (payload.find(
                (p) =>
                  p.type === "project-phases" &&
                  String(p.id) === String(projectPhaseId)
              ) as V2ProjectPhaseResource | undefined)
            : undefined;
          const phaseName = projectPhase?.attributes?.name || "";
          const phasePosition = projectPhase?.attributes?.position ?? 999999;

          const psResource = lookupResource(
            ps.relationships?.["project-resource"]?.data?.id
          );

          // Walk the parent's reflexive `project-subservices` relationship to
          // resolve children from the same payload, preserving declared order.
          const subserviceRefs =
            ps.relationships?.["project-subservices"]?.data || [];
          const subservices: Subservice[] = subserviceRefs
            .map((ref, index) => {
              const sub = payload.find(
                (p) =>
                  p.type === "project-subservices" &&
                  String(p.id) === String(ref.id)
              ) as V2ProjectSubserviceResource | undefined;
              if (!sub) return undefined;
              const subResource = lookupResource(
                sub.relationships?.["project-resource"]?.data?.id
              );
              return {
                id: `${ps.id}-sub-${sub.id ?? index}`,
                type: "subservices" as "subservices",
                serviceType: serviceType as Subservice["serviceType"],
                name: sub.attributes?.name || "",
                quantity: String(sub.attributes?.quantity ?? ""),
                hours:
                  sub.attributes?.["override-hours"] != null
                    ? Number(sub.attributes["override-hours"]).toFixed(1)
                    : "",
                resource: subResource || psResource,
                serviceId: String(ps.id),
              };
            })
            .filter((s) => s !== undefined) as Subservice[];

          return {
            id: String(ps.id),
            type: "services",
            serviceType,
            name: ps.attributes?.name || "",
            quantity: String(ps.attributes?.quantity ?? ""),
            hours:
              ps.attributes?.["override-hours"] != null
                ? Number(ps.attributes["override-hours"]).toFixed(1)
                : "",
            resource: psResource,
            phase:
              serviceType === "professional_services"
                ? {
                    id: phaseIdFor(phaseName),
                    name: phaseName,
                    position: phasePosition,
                  }
                : { id: -1, name: "Managed Services", position: 999999 },
            subservices,
          };
        });

        const phases = Array.from(
          new Map(
            services
              .map((item) => item.phase)
              .map((phase) => [phase?.id, phase])
          ).values()
        ).sort((a, b) => {
          if (a?.id === -1) return 1;
          if (b?.id === -1) return -1;
          return a?.position! - b?.position! || 0;
        });

        const organizedServices = phases
          .map((p) => services.filter((s) => s?.phase?.id == p?.id) || [])
          .flat();

        const formatHours = (value: unknown): string =>
          value != null && value !== "" ? Number(value).toFixed(1) : "";
        const formatString = (value: unknown): string =>
          value != null && value !== "" ? String(value) : "";

        const projectRecord = payload.find((p) => p.type === "projects") as
          | V2ProjectResource
          | undefined;

        let general: BlueprintGeneral | undefined;
        if (projectRecord) {
          const rateTableId =
            projectRecord.relationships?.["rate-table"]?.data?.id;
          const paymentTermId =
            projectRecord.relationships?.["payment-term"]?.data?.id;

          const candidate: BlueprintGeneral = {
            executiveSummary: projectRecord.attributes?.["executive-summary"],
            solutionSummary: projectRecord.attributes?.["solution-summary"],
            rateTableId: rateTableId != null ? String(rateTableId) : undefined,
            paymentTermId:
              paymentTermId != null ? String(paymentTermId) : undefined,
          };
          if (Object.values(candidate).some((v) => v != null && v !== "")) {
            general = candidate;
          }
        }

        const governances: BlueprintGovernance[] = (
          payload.filter(
            (p) => p.type === "project-governances"
          ) as V2ProjectGovernanceResource[]
        ).map((g) => ({
          id: String(g.id),
          description: g.attributes?.description,
          hours: formatHours(g.attributes?.hours),
        }));

        const expenses: BlueprintExpense[] = (
          payload.filter(
            (p) => p.type === "project-expenses"
          ) as V2ProjectExpenseResource[]
        ).map((e) => ({
          id: String(e.id),
          description: e.attributes?.description,
          quantity: formatString(e.attributes?.quantity),
          rate: formatString(e.attributes?.rate),
        }));

        const resources: BlueprintResource[] = (
          payload.filter(
            (p) => p.type === "project-resources"
          ) as V2ProjectResourceResource[]
        ).map((r) => ({
          id: String(r.id),
          name:
            (r.attributes as any)?.["extended-name"] ||
            r.attributes?.name ||
            "",
          hours: formatHours(r.attributes?.["total-hours"]),
          rate: formatString(r.attributes?.["hourly-rate"]),
        }));

        const products: BlueprintProduct[] = (
          payload.filter(
            (p) => p.type === "project-products"
          ) as V2ProjectProductResource[]
        ).map((p) => ({
          id: String(p.id),
          name: p.attributes?.name,
          quantity: formatString(p.attributes?.quantity),
          unitPrice: formatString(p.attributes?.["unit-price"]),
        }));

        return {
          id: String(blueprint.id),
          name: blueprint.attributes?.name || "",
          services: organizedServices,
          tags: blueprint?.attributes?.["tag-list"] || [],
          general,
          governances,
          expenses,
          resources,
          products,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }
  return {
    blueprints,
    isLoading,
    error: error ?? customError,
    pageMeta,
    refetch,
    isFetching,
  };
};
export default useListBlueprints;
